/**
 * api.ts — every call this app makes into the e-signing subsystem, in one place.
 *
 * TYPES ARE CAST. The RPCs and the two new tables land on staging after this branch, so
 * `supabase` still has no generated signature for them; `(supabase as any)` is the agreed
 * shape until the regen. The return types declared here are read off the migration and the
 * edge function, not guessed — they are the documentation until the generated ones exist.
 *
 * WHAT IS NOT HERE: anything that writes an `agreement_*` table directly. Every write
 * policy on those six tables is `USING (false)`, so the RPCs are not a convenience layer
 * over PostgREST — they are the only door.
 */

import { supabase } from '@/integrations/supabase/client';
import { composeFunctionErrorMessage, isLiveDocument } from './constants';
import { completionTime, firstEmbed } from './documents';
import type {
    AgreementDocStatus,
    AgreementDocType,
    AgreementParty,
    AgreementSignatureStatus,
} from './constants';

// ─── Shapes ──────────────────────────────────────────────────────────────────

export interface AgreementSignatureRow {
    party: AgreementParty;
    role_label: string | null;
    sign_order: number | null;
    status: AgreementSignatureStatus;
    signer_name: string | null;
    signed_at: string | null;
}

/** One row of `get_agreement_documents`. */
export interface AgreementDocumentRow {
    id: number;
    doc_type: AgreementDocType;
    reference_no: string;
    title: string;
    status: AgreementDocStatus;
    origin: string | null;
    /**
     * NULL ON THE DEPOSIT ARC, PERMANENTLY. A deposit agreement created before the deposit
     * was attached to a contract carries no contract of its own and never will — the owning
     * contract is derived through the arc every time anybody reads it. `get_agreement_documents`
     * hands back the id it was asked for, so this is only ever NULL on the direct read below.
     */
    contract_id: number | null;
    created_at: string;
    frozen_at: string | null;
    completed_at: string | null;
    finalized_at: string | null;
    final_bucket: string | null;
    final_path: string | null;
    /**
     * The folder every object on this document is filed under: `deposit/<contract_deposit_id>`
     * on the deposit arc, `<contract_id>` everywhere else. Derived by `agreement_path_scope`,
     * which is ALSO the function `agreement_sign` compares an uploaded signature path against —
     * so a client that composed the same string a second way could be refused by the very
     * function that produced it. Present before finalization, because the caller that needs it
     * has not finalized: it is composing a path it is about to upload to.
     */
    path_scope: string | null;
    signatures: AgreementSignatureRow[];
}

/**
 * The arc a document sits on. `get_agreement_documents` deliberately carries no row ids,
 * so this slim direct read is what maps a document back to the loan or the deposit it was
 * generated for — a contract can hold several of either.
 */
export interface AgreementDocumentArcs {
    id: number;
    template_id: number | null;
    contract_agreement_id: number | null;
    contract_deposit_agreement_id: number | null;
    loan_agreement_id: number | null;
    insurance_loan_agreement_id: number | null;
    /**
     * THE FIFTH ARC [D-B48] — `contract_partner_agreements`, the Vehicle Schedule's referent.
     *
     * It changes nothing about the OR-filter below: a partner document keeps `contract_id` set
     * (only the deposit arc goes without one), so the contract arm already returns it. What the
     * column is for is the same thing the other four are for — matching a document onto the slot
     * it was generated for, by row id rather than by doc type.
     */
    contract_partner_agreement_id: number | null;
}

/** `agreement_render`. `missing`, `values` and `drifted` are admin-only and non-null here. */
export interface AgreementRenderPayload {
    document_id: number;
    doc_type: AgreementDocType;
    reference_no: string;
    title: string;
    status: AgreementDocStatus;
    frozen: boolean;
    frozen_at: string | null;
    rendered_md: string;
    content_sha256: string;
    parties: AgreementSignatureRow[];
    missing: string[] | null;
    values: Record<string, unknown> | null;
    drifted: boolean | null;
    final_bucket: string | null;
    final_path: string | null;
    /**
     * As on `AgreementDocumentRow`, and deliberately NOT admin-masked the way the three fields
     * above are: those are diagnostics about a render, this is the address an already-admitted
     * reader composes their own paths from.
     */
    path_scope: string | null;
}

export interface AgreementTokenRow {
    id: number;
    document_id: number;
    party: AgreementParty;
    token_prefix: string;
    expires_at: string;
    consumed_at: string | null;
    revoked_at: string | null;
    use_count: number | null;
    created_at: string;
}

export interface IssuedToken {
    document_id: number;
    token_id: number;
    /** The raw token. Returned exactly once, by this call, and never readable again. */
    token: string;
    token_prefix: string;
    expires_at: string;
}

// ─── Reads ───────────────────────────────────────────────────────────────────

export async function fetchAgreementDocuments(contractId: number): Promise<AgreementDocumentRow[]> {
    const { data, error } = await (supabase as any).rpc('get_agreement_documents', {
        p_contract_id: contractId,
    });
    if (error) throw error;
    return (data ?? []) as AgreementDocumentRow[];
}

const ARC_COLUMNS =
    'id, template_id, contract_agreement_id, ' +
    'contract_deposit_agreement_id, loan_agreement_id, insurance_loan_agreement_id, ' +
    'contract_partner_agreement_id';

/**
 * The arcs of every document on one contract — INCLUDING the deposit documents that name no
 * contract of their own.
 *
 * `contract_id` ALONE IS THE WRONG FILTER, AND IT FAILS SILENTLY. A deposit agreement created
 * before its deposit was attached carries `contract_id` NULL permanently: there is no backfill,
 * so the column stays NULL even after the deposit joins a contract and the owning contract is
 * derived through the arc on every read. `get_agreement_documents` performs that join and
 * returns the document; this read, filtered on the column, returned no arc row for it — and the
 * board matches a document to its slot BY ARC ID, so the deposit row showed "Create agreement"
 * for a deposit whose document already exists, and a click landed on the RPC's `created: false`.
 *
 * THE JOIN IS EXPRESSED AS TWO STEPS RATHER THAN A NESTED EMBED FILTER. The deposit-agreement
 * ids are read off `contract_deposits` first — the same shape the board already reads that
 * table in — and the documents are then fetched by contract OR by those ids. A document created
 * after assignment satisfies both arms; PostgREST returns one row per document either way.
 */
export async function fetchAgreementDocumentArcs(
    contractId: number,
): Promise<AgreementDocumentArcs[]> {
    const { data: deposits, error: depositsError } = await (supabase as any)
        .from('contract_deposits')
        .select('contract_deposit_agreements(id)')
        .eq('contract_id', contractId);
    if (depositsError) throw depositsError;

    const depositArcIds = ((deposits ?? []) as Array<{
        contract_deposit_agreements: { id: number } | { id: number }[] | null;
    }>).flatMap((row) => {
        const arc = row.contract_deposit_agreements;
        if (!arc) return [];
        return (Array.isArray(arc) ? arc : [arc]).map((entry) => entry.id);
    });

    const documents = (supabase as any).from('agreement_documents').select(ARC_COLUMNS);
    const { data, error } = await (depositArcIds.length > 0
        ? documents.or(
              `contract_id.eq.${contractId},` +
              `contract_deposit_agreement_id.in.(${depositArcIds.join(',')})`,
          )
        : documents.eq('contract_id', contractId));
    if (error) throw error;
    return (data ?? []) as AgreementDocumentArcs[];
}

/** What one reference resolves to. Only the arc back to the legacy row is read off it. */
export interface AgreementDocumentReference {
    id: number;
    contract_id: number | null;
    contract_agreement_id: number | null;
}

/**
 * The one document a reference names, or null when no document carries it.
 *
 * SINGULAR BY INDEX, NOT BY HOPE. `agreement_documents_reference_no_unique` is what makes
 * `maybeSingle` the right shape here — a second row with the same reference is impossible, so a
 * caller never has to decide which of two documents an admin meant.
 *
 * EQUALITY, NEVER `ilike`. A reference is an identifier: the caller canonicalises what was typed
 * (`referenceCheck.parseAgreementSearch`) and this looks for exactly that. A prefix match would
 * hand back a different agreement than the one printed on the paper in the admin's hand.
 *
 * NO `as any` ON THIS ONE, UNLIKE ITS NEIGHBOURS. The three columns it names all exist in the
 * generated types; the casts elsewhere in this file are for RPCs and columns that the regen has
 * not caught up with, not a house style to copy.
 */
export async function fetchAgreementDocumentByReference(
    referenceNo: string,
): Promise<AgreementDocumentReference | null> {
    const { data, error } = await supabase
        .from('agreement_documents')
        .select('id, contract_id, contract_agreement_id')
        .eq('reference_no', referenceNo)
        .maybeSingle();
    if (error) throw error;
    return data ?? null;
}

/** The legacy `contract_deposit_agreements` row — the arc every deposit document hangs off. */
export interface DepositAgreementArc {
    id: number;
    signed_at: string | null;
    agreement_url: string | null;
    origin: string | null;
}

export interface DepositAgreement {
    /** Null until something — a generated document or a paper upload — has created it. */
    arc: DepositAgreementArc | null;
    /** The one live document on the arc. VOIDED and SUPERSEDED ones are never live. */
    document: AgreementDocumentRow | null;
    /**
     * The depositor's frozen phone number, carried separately because `AgreementDocumentRow`
     * is the shape `get_agreement_documents` returns and that function does not report it.
     * Void-and-reissue needs it: `agreement_create` requires a phone for every DEPOSIT
     * document, so a replacement that could not carry the number an admin already typed would
     * be refused outright — and on an unattached deposit there is no other record of it.
     */
    depositorPhone: string | null;
    /**
     * The depositor's frozen address, carried for the same reason and needed by the same path.
     * On an unattached deposit the document IS the record of it — the payer has no `profiles`
     * row to read it back from — so a reissue that dropped it would replace a schedule that
     * states an address with one that reports a fill-in.
     */
    depositorAddress: string | null;
}

/**
 * Raw shape of the document read before its embeds are flattened. PostgREST answers a to-one
 * embed as an object and a to-many as an array, so both readings of the template are accepted.
 */
interface RawDepositDocument {
    id: number;
    doc_type: AgreementDocType;
    reference_no: string;
    status: AgreementDocStatus;
    contract_id: number | null;
    created_at: string;
    frozen_at: string | null;
    finalized_at: string | null;
    agreement_templates: { title: string } | { title: string }[] | null;
    agreement_signatures: Array<{
        party: AgreementParty;
        sign_order: number | null;
        status: AgreementSignatureStatus;
        signer_name: string | null;
        signer_phone: string | null;
        signer_address: string | null;
        signed_at: string | null;
    }> | null;
}

/**
 * Everything the deposit sheet needs about one deposit's agreement.
 *
 * IT CANNOT GO THROUGH `get_agreement_documents`, AND THAT IS THE WHOLE POINT OF THIS
 * FEATURE. That function is keyed on a contract, and the deposits this block exists for were
 * paid before any contract existed — their documents carry `contract_id` NULL until the
 * deposit is assigned. So the read walks the arc instead: the deposit names its
 * `contract_deposit_agreements` row, and that row is what the document points at.
 * `agreement_documents_select` and `agreement_signatures_select` both carry an `is_admin()`
 * arm, so an admin session reads both directly even while the deposit belongs to nobody.
 *
 * FOUR FIELDS ARE DERIVED, NOT SELECTED. `title`, `origin`, `completed_at` and the executed
 * PDF's location stopped being columns — the server derives them, and so does this: the title
 * from the pinned template relation, the completion time through the one shared rule in
 * `documents.ts`.
 *
 * THE THREE PATH FIELDS ARE LEFT NULL ON PURPOSE. `final_bucket`, `final_path` and `path_scope`
 * are all answers `agreement_path_scope` gives, and the block already holds an `agreement_render`
 * payload that carries them from the server. Composing `generated/deposit/<deposit>/<document>/`
 * here would be a second copy of a rule that has already moved once — and the copy that decides
 * whether an upload is ACCEPTED is the server's, so a client that guessed differently would be
 * refused by the function that produced the address it guessed at.
 */
export async function fetchDepositAgreement(depositId: number): Promise<DepositAgreement> {
    const { data: arc, error: arcError } = await (supabase as any)
        .from('contract_deposit_agreements')
        .select('id, signed_at, agreement_url, origin')
        .eq('contract_deposit_id', depositId)
        .maybeSingle();
    if (arcError) throw arcError;
    if (!arc) return { arc: null, document: null, depositorPhone: null, depositorAddress: null };

    const { data, error } = await (supabase as any)
        .from('agreement_documents')
        .select(
            'id, doc_type, reference_no, status, contract_id, created_at, frozen_at, finalized_at, ' +
            'agreement_templates(title), ' +
            'agreement_signatures(party, sign_order, status, signer_name, signer_phone, signer_address, signed_at)',
        )
        .eq('contract_deposit_agreement_id', arc.id)
        .order('id', { ascending: false });
    if (error) throw error;

    const live =
        ((data ?? []) as RawDepositDocument[]).find((row) => isLiveDocument(row.status)) ?? null;
    if (!live) return { arc: arc as DepositAgreementArc, document: null, depositorPhone: null, depositorAddress: null };

    const template = firstEmbed(live.agreement_templates);

    const rawSignatures = live.agreement_signatures ?? [];
    const signatures: AgreementSignatureRow[] = rawSignatures
        .map((signature) => ({
            party: signature.party,
            // Dropped as a column; only the pinned template knows it, and nothing on this
            // surface prints it — the party labels do that job.
            role_label: null,
            sign_order: signature.sign_order ?? null,
            status: signature.status,
            signer_name: signature.signer_name ?? null,
            signed_at: signature.signed_at ?? null,
        }))
        .sort((a, b) => (a.sign_order ?? 0) - (b.sign_order ?? 0));

    return {
        arc: arc as DepositAgreementArc,
        // BOTH COME OFF THE SAME ROW, found the same party-agnostic way the block finds the
        // depositor's chip: the deposit template seats DRIVER first today, and a future version
        // that renamed that seat should still hand a reissue the identity it needs.
        depositorPhone:
            rawSignatures.find((signature) => signature.party !== 'COMPANY_REP')?.signer_phone ?? null,
        depositorAddress:
            rawSignatures.find((signature) => signature.party !== 'COMPANY_REP')?.signer_address ?? null,
        document: {
            id: live.id,
            doc_type: live.doc_type,
            reference_no: live.reference_no,
            title: template?.title ?? 'Deposit Agreement',
            status: live.status,
            origin: 'IN_APP',
            contract_id: live.contract_id ?? null,
            created_at: live.created_at,
            frozen_at: live.frozen_at ?? null,
            // The server's own rule, shared rather than restated: max(signed_at) over the rows
            // that still count, and only once every one of them is SIGNED.
            completed_at: completionTime(signatures),
            finalized_at: live.finalized_at ?? null,
            final_bucket: null,
            final_path: null,
            path_scope: null,
            signatures,
        },
    };
}

export async function renderAgreement(documentId: number): Promise<AgreementRenderPayload> {
    const { data, error } = await (supabase as any).rpc('agreement_render', {
        p_document_id: documentId,
    });
    if (error) throw error;
    return data as AgreementRenderPayload;
}

/**
 * The template body a document was generated from, WITH ITS MERGE FIELDS STILL IN IT. This is
 * the ONLY input from which every field can be marked, because `agreement_render` returns text
 * whose fields are already substituted. It is a review affordance and is never hashed;
 * `rendered_md` stays canonical.
 *
 * THE PINNED TEMPLATE IS THE SNAPSHOT — there is no second copy to prefer over it. A per-document
 * `frozen_body_md` used to hold one, and it could never differ: `template_id` is write-once under
 * `agreement_documents_write_once` and a published template version is immutable, so the body
 * behind it is the body the document was generated from whether or not the document has frozen.
 * The column was dropped for exactly that reason.
 */
export async function fetchDocumentBody(documentId: number): Promise<string | null> {
    const { data, error } = await (supabase as any)
        .from('agreement_documents')
        .select('agreement_templates(body_md)')
        .eq('id', documentId)
        .maybeSingle();
    if (error) throw error;
    if (!data) return null;
    const template = (data as any).agreement_templates;
    const templateBody = Array.isArray(template) ? template[0]?.body_md : template?.body_md;
    return templateBody ?? null;
}

export async function fetchAgreementTokens(documentIds: number[]): Promise<AgreementTokenRow[]> {
    if (documentIds.length === 0) return [];
    const { data, error } = await (supabase as any)
        .from('agreement_signing_tokens')
        .select('id, document_id, party, token_prefix, expires_at, consumed_at, revoked_at, use_count, created_at')
        .in('document_id', documentIds)
        .order('created_at', { ascending: false });
    if (error) throw error;
    return (data ?? []) as AgreementTokenRow[];
}

// ─── Writes (all through the RPCs) ───────────────────────────────────────────

/**
 * Who paid a deposit, as the document will print them.
 *
 * THE NAME IS THE IDENTITY, NOT A LABEL. A deposit is routinely paid weeks before a vehicle
 * is allocated, so the depositor frequently has no driver row and no account: there is
 * nothing for the signature to point at, and `agreement_create` freezes these two values
 * onto the DRIVER signature seat instead — `agreement_merge_data` then reads the deposit
 * document's `driver.full_name` and `driver.phone` from that row and from nowhere else.
 *
 * WHAT THE RPC ACTUALLY INSISTS ON, WHICH IS NOT WHAT THIS APP INSISTS ON.
 * `agreement_create` raises AGREEMENT_SIGNER_REQUIRED for a DEPOSIT document on a deposit
 * with NO contract and no name — the one case where nothing else can name the signer. It
 * never requires the phone, and it IGNORES both parameters where the deposit's contract
 * driver holds an account, because binding the seat to that profile is what lets them sign
 * in their own session. Either parameter on any other document type is
 * AGREEMENT_SIGNER_UNEXPECTED, where the signer comes from the contract and a name passed
 * here would print over the one on record.
 *
 * THE CREATE FORM ASKS FOR ALL THREE ANYWAY, AND THAT IS THIS APP'S RULE RATHER THAN SQL'S.
 * `driver.full_name` and `driver.phone` are both REQUIRED MERGE FIELDS on the deposit
 * template, so a document generated without a phone is accepted by the RPC and then reports
 * it in `missing[]` — it prints a blank line and cannot be sent. The address is the same
 * shape of problem one document version ahead: the payer holds no `profiles` row, so the
 * address typed here is the ONLY one `driver.address` can ever resolve. Asking up front is a
 * disabled button instead of a document that has to be voided.
 */
export interface DepositorIdentity {
    name: string;
    /** Optional to the RPC. The template needs it, so the create form still insists on it. */
    phone: string | null;
    /**
     * Optional to the RPC for the same reason the phone is, and asked for by the form for a
     * stronger one: a payer with no account has no `profiles.address` behind them, so this is
     * the only address a deposit schedule can print for them.
     */
    address: string | null;
}

/**
 * Generate the document for one subject.
 *
 * THE VEHICLE SCHEDULE TAKES NO FIGURE FROM THIS CALL [D-B59]. Its Cash price is the contract's
 * own `contracts.cash_price`: `agreement_create` copies it, rounded to the pesewa, onto the
 * referent (`contract_partner_agreements.all_in_cost`, a column that keeps its historical name) at
 * create and on every re-issue over an unsealed slot with no live document, and refuses with
 * `AGREEMENT_CASH_PRICE_REQUIRED` where the contract states no cash price or one of nothing. The
 * figure is therefore fixed on Financials, and a replacement always states the contract's current
 * one.
 *
 * `agreement_create` TAKES FIVE ARGUMENTS and none of them is a figure: the document type, the
 * subject and the three depositor keys below. There is no way to state a different Cash price
 * through this call.
 */
export async function createAgreement(
    docType: AgreementDocType,
    subjectId: number,
    depositor?: DepositorIdentity | null,
): Promise<{ document_id: number; created: boolean; reference_no?: string }> {
    const name = depositor?.name?.trim() || null;
    const phone = depositor?.phone?.trim() || null;
    const address = depositor?.address?.trim() || null;

    const { data, error } = await (supabase as any).rpc('agreement_create', {
        p_doc_type: docType,
        p_subject_id: subjectId,
        // THE KEYS ARE OMITTED, NOT SENT AS NULL. All three parameters carry DEFAULT NULL, so a
        // call that names none of them is byte-for-byte the call every existing caller has
        // always made — which is what keeps the board, void-and-reissue and the deposit block on
        // one function rather than on several argument shapes.
        //
        // THE PHONE AND THE ADDRESS TRAVEL WITH THE NAME AND NEVER ALONE. `agreement_create`
        // freezes the external identity only where a NAME arrives — `signer_external` is
        // computed from it — so either of them by itself is discarded on the deposit arc and is
        // AGREEMENT_SIGNER_UNEXPECTED on every other type: a round trip that can only fail or do
        // nothing.
        ...(name
            ? {
                  p_signer_name: name,
                  ...(phone ? { p_signer_phone: phone } : {}),
                  ...(address ? { p_signer_address: address } : {}),
              }
            : {}),
    });
    if (error) throw error;
    return data;
}

/**
 * A signing link for one of the two parties that sign by link — and they are opposites.
 *
 * WITNESS attests to something that already exists: frozen content and a signature already
 * taken. `p_content_sha256` is therefore the hash the ADMIN was shown, and on a document
 * nobody has frozen yet, issuing the link is what freezes it — through the same
 * compare-or-refuse path a signature takes.
 *
 * DRIVER is the depositor's link, and the depositor is the FIRST signer. Nothing is frozen
 * at issue and no hash is required: `agreement_token_sign` freezes against the hash the
 * depositor's own browser computed over the text their own ceremony rendered, which is the
 * only hash that is evidence about what that human read. An admin's hash, taken days
 * earlier, is not. `agreement_issue_token` mints a DRIVER link on the deposit arc and
 * refuses it everywhere else, where the driver signs in their own session.
 */
export async function issueSigningToken(args: {
    documentId: number;
    party: 'WITNESS' | 'DRIVER';
    /** Required for WITNESS. On a DRIVER link this is only a consistency check, so null is normal. */
    contentSha256: string | null;
}): Promise<IssuedToken> {
    const { data, error } = await (supabase as any).rpc('agreement_issue_token', {
        p_document_id: args.documentId,
        p_party: args.party,
        p_content_sha256: args.contentSha256,
    });
    if (error) throw error;
    return data as IssuedToken;
}

export function issueWitnessToken(
    documentId: number,
    contentSha256: string | null,
): Promise<IssuedToken> {
    return issueSigningToken({ documentId, party: 'WITNESS', contentSha256 });
}

export async function revokeToken(tokenId: number, reason: string | null) {
    const { data, error } = await (supabase as any).rpc('agreement_revoke_token', {
        p_token_id: tokenId,
        p_reason: reason,
    });
    if (error) throw error;
    return data as { token_id: number; revoked: boolean };
}

/**
 * The company countersignature. `p_signature_path` is the admin's saved signature at
 * `admins/<profile_id>.png` — `agreement_sign` re-checks that the object exists AND that
 * this session uploaded it, which is what stops one admin signing with another's ink.
 */
export async function countersignAgreement(args: {
    documentId: number;
    contentSha256: string;
    typedName: string;
    signaturePath: string;
    consentVersion: string;
}) {
    const { data, error } = await (supabase as any).rpc('agreement_sign', {
        p_document_id: args.documentId,
        p_party: 'COMPANY_REP',
        p_content_sha256: args.contentSha256,
        p_typed_name: args.typedName,
        p_signature_path: args.signaturePath,
        p_consent_text_version: args.consentVersion,
        p_user_agent: typeof navigator === 'undefined' ? null : navigator.userAgent,
    });
    if (error) throw error;
    return data as { document_id: number; signed: boolean; status: AgreementDocStatus };
}

/**
 * Withdraw a document. THE ROW IS DELETED, NOT SET TO 'VOIDED' [D-B40] — which is why the answer
 * carries `deleted` and the addresses of the storage objects the delete has just made unreachable
 * rather than a status. The last two keys belong to the delete branch alone: a document id that
 * resolves to nothing answers `deleted: false, changed: false` and nothing else, so a retry after
 * a half-finished void is not wedged by its own first attempt.
 */
export async function voidAgreement(documentId: number, reason: string | null) {
    const { data, error } = await (supabase as any).rpc('agreement_void', {
        p_document_id: documentId,
        p_reason: reason,
    });
    if (error) throw error;
    return data as {
        document_id: number;
        deleted: boolean;
        changed: boolean;
        /**
         * Bytes no row addresses any more. `[]` when it owned none. Reported rather than acted
         * on here: the ledger row this answer echoes is what `sweepOrphans` drains afterwards,
         * from the whole table rather than from one void's share of it.
         */
        orphaned_objects?: { bucket: string; path: string }[];
        /** A deposit agreement's frozen external identity — it exists nowhere else. Null elsewhere. */
        depositor?: { name: string | null; phone: string | null; address: string | null } | null;
    };
}

// ─── Edge function: generate-agreement ───────────────────────────────────────

/**
 * The message behind a `FunctionsHttpError`.
 *
 * The function reports its own failures as JSON with a non-2xx status, and supabase-js
 * collapses that into "non-2xx status code" — so the body is read back off `error.context`
 * and the admin sees "every party must sign before finalizing" instead.
 */
async function functionError(error: unknown, fallback: string): Promise<Error> {
    const response = (error as { context?: Response }).context;
    const body = response?.json ? await response.json().catch(() => null) : null;
    if (body?.error) return new Error(composeFunctionErrorMessage(body));
    if (response?.status) return new Error(`${fallback} (${response.status})`);
    return error instanceof Error ? error : new Error(fallback);
}

export interface FinalizeResult {
    ok: boolean;
    already_finalized: boolean;
    reference_no: string;
    final_bucket: string | null;
    final_path: string | null;
    page_count: number | null;
    agreement_url?: string | null;
}

export async function finalizeAgreement(documentId: number): Promise<FinalizeResult> {
    const { data, error } = await supabase.functions.invoke('generate-agreement', {
        body: { action: 'finalize', document_id: documentId },
    });
    if (error) throw await functionError(error, 'Could not finalize the document');
    if ((data as any)?.error) throw new Error((data as any).error);
    return data as FinalizeResult;
}

/**
 * Sweep the bytes a withdrawal stranded [D-B44]. FIRE AND FORGET, AND SILENT BY CONSTRUCTION.
 *
 * `agreement_void` records the addresses it can no longer compose and cannot delete the objects —
 * `storage.protect_delete()` refuses a SQL delete outright. This is the other half, and it is
 * invisible: no toast, no spinner, no failure the admin can act on. A sweep that fails leaves the
 * ledger row where it was and the next void's sweep drains it, so the list is self-healing and
 * there is nothing here to retry by hand.
 *
 * IT NEVER THROWS, AND THAT IS THE CONTRACT EVERY CALL SITE IS WRITTEN AGAINST. Every caller is a
 * gesture that has ALREADY succeeded — the document is voided, the money is written — so a
 * refusal from this call would report a failure of something that did not fail. Both arms are
 * swallowed: the thrown one, and the `{ error }` a non-2xx answer arrives as.
 */
export async function sweepOrphans(): Promise<void> {
    try {
        await supabase.functions.invoke('generate-agreement', {
            body: { action: 'sweep_orphans' },
        });
    } catch {
        /* swallowed on purpose — see above. */
    }
}

/**
 * `preview` and `my_signed_copy` answer with `application/pdf`, which functions-js decodes
 * with `response.blob()` — so the bytes survive `invoke` and the call carries the session's
 * JWT the same way every other call here does. (The driver app's `useSignedCopyDownload`
 * reads the same action the same way.)
 *
 * A response that is NOT a Blob is a failure the function chose to report with a 200, so it
 * is raised rather than handed to the browser as a download of an error message.
 */
async function invokePdf(body: Record<string, unknown>, action: string): Promise<Blob> {
    const { data, error } = await supabase.functions.invoke('generate-agreement', {
        body: { ...body, action },
    });
    if (error) throw await functionError(error, 'Could not render the document');
    if (!(data instanceof Blob)) {
        const failure = data as { error?: string } | null;
        throw new Error(failure?.error ?? 'The document did not come back as a PDF.');
    }
    return data;
}

export function previewAgreementPdf(documentId: number, highlightMergeFields: boolean) {
    return invokePdf({ document_id: documentId, highlight_merge_fields: highlightMergeFields }, 'preview');
}

export function downloadSignedCopy(documentId: number) {
    return invokePdf({ document_id: documentId }, 'my_signed_copy');
}

/**
 * Hands a Blob to the browser as a download without leaking the object URL.
 *
 * The revoke is deferred rather than immediate: some browsers read the URL asynchronously
 * after the synthetic click, and revoking in the same tick cancels the download.
 */
export function saveBlob(blob: Blob, filename: string) {
    const url = URL.createObjectURL(blob);
    const anchor = document.createElement('a');
    anchor.href = url;
    anchor.download = filename;
    document.body.appendChild(anchor);
    anchor.click();
    anchor.remove();
    setTimeout(() => URL.revokeObjectURL(url), 60_000);
}
